/**
 * Segment Assembly Utilities
 *
 * Functions for assembling draft segments into a TipTap document,
 * detecting segment boundaries, and extracting changed segments.
 *
 * Spec 08 §10.3-§10.5
 */

import type { JSONContent, Editor } from '@tiptap/core'
import { generateJSON, generateHTML } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import { Table as TableExtension } from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import { unified } from 'unified'
import remarkParse from 'remark-parse'
import remarkGfm from 'remark-gfm'
import remarkRehype from 'remark-rehype'
import rehypeStringify from 'rehype-stringify'
import { stripAnnotations } from '@/lib/annotations'
import { SegmentHeading } from './SegmentHeading'
import { diffMarks } from './diffMarks'

// ===================
// Types
// ===================

export interface DraftSegmentSummary {
  id: string
  sessionId: string
  sectionId: string
  heading: string
  content: string
  status: 'fresh' | 'stale' | 'generating' | 'locked'
  version: number
  wordCount: number
  editedWhileStale: boolean
  coverageRefs: Array<{ requirementId: string; confidence: number }>
  citationRefs: Array<{ citationIndex: number; documentId: string; documentName: string; chunkIndex: number; pageNumber: number | null }>
  createdAt: string
  updatedAt: string
}

// Extensions used for HTML parsing (must match editor extensions)
const parseExtensions = [
  StarterKit,
  TableExtension,
  TableRow,
  TableCell,
  TableHeader,
  SegmentHeading,
  ...diffMarks,
]

function looksLikeHtml(input: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(input)
}

/**
 * Detect mixed HTML/Markdown content — HTML tags present but also raw
 * Markdown syntax (headings or tables) that aren't inside HTML tags.
 *
 * Ported line for line to `backend/src/lib/mixed-content.ts` (with
 * `normalizeMixedContent` and `convertMarkdownTables`), so the DOCX export and
 * the Full response read these rows as the editor does. Change both together.
 */
function hasMixedFormats(input: string): boolean {
  if (!looksLikeHtml(input)) return false
  // Markdown headings outside HTML tags
  if (/^#{1,6}\s+/m.test(input)) return true
  // Markdown table separator row (e.g. |---|:---:|)
  if (/^\|[\s\-:|]+\|\s*$/m.test(input)) return true
  return false
}

function markdownToHtmlPreserveCitations(markdown: string): string {
  return String(
    unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkRehype)
      .use(rehypeStringify)
      .processSync(markdown)
  )
}

/**
 * Convert a GFM-style Markdown table (| ... | ... | rows + separator row)
 * into an HTML <table>. Only the contiguous table block is replaced.
 */
function convertMarkdownTables(input: string): string {
  const tableRegex = /(^\|.+\|[ \t]*\r?\n)(^\|[ \t\-:|]+\|[ \t]*\r?\n)((?:^\|.+\|[ \t]*\r?\n?)+)/gm

  return input.replace(tableRegex, (_match, headerLine: string, _sep: string, bodyBlock: string) => {
    const parseCells = (row: string): string[] =>
      row.trim().replace(/^\||\|$/g, '').split('|').map((cell) => cell.trim())

    const headerCells = parseCells(headerLine)
    const bodyRows = bodyBlock.split(/\r?\n/).filter((line) => line.trim().length > 0)

    const thead = `<thead><tr>${headerCells.map((cell) => `<th>${cell}</th>`).join('')}</tr></thead>`
    const tbody = `<tbody>${bodyRows
      .map((row) => {
        const cells = parseCells(row)
        return `<tr>${cells.map((cell) => `<td>${cell}</td>`).join('')}</tr>`
      })
      .join('')}</tbody>`

    return `<table>${thead}${tbody}</table>\n`
  })
}

/**
 * Convert Markdown portions within HTML content to HTML.
 * Handles mixed-format content from subsection patch stitching where
 * the patched subsection is HTML but surrounding content reverted to Markdown.
 */
function normalizeMixedContent(input: string): string {
  // Tables span multiple lines — convert them first before the line-based passes
  // would otherwise wrap each pipe row in <p>.
  return convertMarkdownTables(input)
    .replace(/^(#{2})\s+(.+)$/gm, '<h2>$2</h2>')
    .replace(/^(#{3})\s+(.+)$/gm, '<h3>$2</h3>')
    .replace(/^(#{4})\s+(.+)$/gm, '<h4>$2</h4>')
    .replace(/^- \*\*(.+?):\*\*\s*(.*)$/gm, '<li><strong>$1:</strong> $2</li>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    // Wrap orphaned text (not inside tags) in <p> tags
    .replace(/^([^<\n].+)$/gm, (line) => {
      // Skip if it's already an HTML element, empty, or a stray table pipe row
      if (/^\s*</.test(line) || /^\s*$/.test(line) || /^\s*\|/.test(line)) return line
      return `<p>${line}</p>`
    })
    // Markdown bold left in the HTML part of the row ("**Bold label:** …")
    // printed its asterisks in the export and the editor alike (R3 F1).
    .replace(/\*\*(?=\S)([^*<>\n]+?)\*\*/g, (match, inner: string) =>
      /\s$/.test(inner) ? match : `<strong>${inner}</strong>`)
}

/**
 * Normalize rich text content for TipTap parsing/insertion.
 * Stored segments are HTML; older/newer AI paths may still return markdown.
 * Mixed-format content (from subsection patches) is normalized to HTML.
 */
export function normalizeRichTextForEditor(content: string): string {
  const trimmed = content.trim()
  if (!trimmed) return ''
  if (hasMixedFormats(trimmed)) return normalizeMixedContent(trimmed)
  return looksLikeHtml(trimmed)
    ? trimmed
    : markdownToHtmlPreserveCitations(trimmed)
}

// ===================
// Document Assembly
// ===================

/**
 * Assemble draft segments into a single TipTap JSONContent document.
 * Segments are ordered by planSectionOrder — Spec 08 §10.3.
 *
 * @param documentSectionNumber - 1-based position of this section in the document outline.
 *   Used to display correct section numbers (e.g., "2." for the 2nd document section).
 */
export function assembleEditorDocument(
  segments: DraftSegmentSummary[],
  planSectionOrder: string[],
  documentSectionNumber?: number
): JSONContent {
  // Map with planSectionOrder index to preserve correct section numbers
  // even when some sections don't have content yet
  const indexed = planSectionOrder
    .map((sectionId, index) => {
      const segment = segments.find((s) => s.sectionId === sectionId)
      // Use documentSectionNumber for the display number when available.
      // For single-segment plans (typical), all segments show the document section number.
      // For multi-segment plans, offset from the document section number.
      const sectionNumber = documentSectionNumber ?? (index + 1)
      return segment ? { segment, sectionNumber } : null
    })
    .filter((item): item is { segment: DraftSegmentSummary; sectionNumber: number } => item !== null)

  const content: JSONContent[] = []

  for (const { segment, sectionNumber } of indexed) {
    // Segment heading — non-editable atom node (from plan)
    content.push({
      type: 'segmentHeading',
      attrs: {
        sectionId: segment.sectionId,
        sectionNumber: String(sectionNumber),
        status: segment.status,
        headingText: segment.heading,
      },
    })

    if (segment.status === 'generating') {
      // Show streaming placeholder
      content.push({
        type: 'paragraph',
        attrs: { class: 'segment-generating' },
        content: segment.content
          ? [{ type: 'text', text: segment.content }]
          : [{ type: 'text', text: 'Generating...' }],
      })
    } else if (segment.content) {
      // Parse stored HTML into ProseMirror nodes
      const parsed = parseHTMLToNodes(segment.content)
      const startsWithSegmentHeading = matchesLeadingHeading(parsed, segment.heading)

      if (!startsWithSegmentHeading && segment.heading.trim().length > 0) {
        // Some persisted drafts may miss their top-level section heading.
        // Reinsert a normal editable h2 heading with section number in the assembled editor document.
        content.push({
          type: 'heading',
          attrs: { level: 2 },
          content: [{ type: 'text', text: `${sectionNumber}. ${segment.heading}` }],
        })
      }

      if (parsed.length > 0) {
        content.push(...parsed)
      } else {
        content.push({ type: 'paragraph', content: [] })
      }
    } else {
      // Empty segment placeholder
      content.push({ type: 'paragraph', content: [] })
    }
  }

  // TipTap requires at least one block node
  if (content.length === 0) {
    content.push({ type: 'paragraph', content: [] })
  }

  return { type: 'doc', content }
}

/**
 * Parse an HTML string into ProseMirror JSONContent nodes using TipTap's generateJSON.
 * Returns the inner content array (without the wrapping doc node).
 */
function parseHTMLToNodes(html: string): JSONContent[] {
  if (!html.trim()) return []
  try {
    const normalized = normalizeRichTextForEditor(html)
    const doc = generateJSON(normalized, parseExtensions)
    const content = doc['content']
    return Array.isArray(content) ? content : []
  } catch {
    // Fallback: treat as plain text paragraph
    return [{ type: 'paragraph', content: [{ type: 'text', text: html.replace(/<[^>]+>/g, '') }] }]
  }
}

function matchesLeadingHeading(nodes: JSONContent[], expectedHeading: string): boolean {
  const first = nodes[0]
  if (!first || first.type !== 'heading') return false

  const firstText = extractTextFromNode(first)
  const normalizedFirst = normalizeHeading(firstText)
  const normalizedExpected = normalizeHeading(expectedHeading)

  // Exact match
  if (normalizedFirst === normalizedExpected) return true

  // Match with section number prefix (e.g., "5. Executive Summary" matches "Executive Summary")
  const withoutNumber = normalizedFirst.replace(/^\d+\.\s*/, '')
  return withoutNumber === normalizedExpected
}

function normalizeHeading(text: string): string {
  return text.trim().replace(/\s+/g, ' ').toLowerCase()
}

function extractTextFromNode(node: JSONContent): string {
  if (node.type === 'text' && typeof node.text === 'string') return node.text
  if (!Array.isArray(node.content)) return ''
  return node.content.map(extractTextFromNode).join('')
}

// ===================
// Segment Boundary Detection
// ===================

/**
 * Extract segment content from the editor by walking segment boundaries.
 * Returns a Map of sectionId → HTML content string — Spec 08 §10.4.
 */
export function extractSegmentsFromEditor(editor: Editor): Map<string, string> {
  const segments = new Map<string, string>()
  const doc = editor.getJSON()

  let currentSectionId: string | null = null
  const currentNodes: JSONContent[] = []

  function flushSegment() {
    if (!currentSectionId) return
    if (currentNodes.length === 0) {
      segments.set(currentSectionId, '')
      return
    }
    // Serialize nodes back to HTML using generateHTML
    const docJson: JSONContent = { type: 'doc', content: currentNodes }
    try {
      const html = generateHTML(docJson, parseExtensions)
      segments.set(currentSectionId, html)
    } catch {
      segments.set(currentSectionId, '')
    }
    currentNodes.length = 0
  }

  for (const node of doc.content ?? []) {
    if (node.type === 'segmentHeading') {
      flushSegment()
      currentSectionId = (node.attrs?.['sectionId'] as string) ?? null
    } else if (currentSectionId) {
      currentNodes.push(node)
    }
  }
  flushSegment()

  return segments
}

/**
 * Detect which segments have changed by comparing current editor content
 * against the previously known segment content map — Spec 08 §10.4.
 */
export function detectChangedSegments(
  editor: Editor,
  previousSegments: Map<string, string>
): Array<{ sectionId: string; content: string; wordCount: number }> {
  const changed: Array<{ sectionId: string; content: string; wordCount: number }> = []
  const currentSegments = extractSegmentsFromEditor(editor)

  for (const [sectionId, content] of currentSegments) {
    if (previousSegments.get(sectionId) !== content) {
      changed.push({
        sectionId,
        content,
        wordCount: countWords(content),
      })
    }
  }

  // If a previously known segment boundary is removed from the editor
  // (e.g. Select All + Delete), persist that segment as emptied content
  // instead of silently skipping the save.
  for (const [sectionId, previousContent] of previousSegments) {
    if (!currentSegments.has(sectionId) && previousContent !== '') {
      changed.push({
        sectionId,
        content: '',
        wordCount: 0,
      })
    }
  }

  return changed
}

/**
 * Count words in an HTML string.
 *
 * Strips author-only annotations (`[TODO: …]`, `[ATT: …]`, legacy attributions)
 * BEFORE tags, so a gap marker never inflates a word count the author is
 * budgeting against (spec 19). Annotation-free content passes through
 * `stripAnnotations` byte-identical, so existing counts are unaffected.
 *
 * Mirrors the backend `countHtmlWords` in `backend/src/lib/word-count.ts`.
 */
export function countWords(html: string): number {
  const text = stripAnnotations(html).replace(/<[^>]+>/g, ' ')
  return text.trim().split(/\s+/).filter((w) => w.length > 0).length
}

/**
 * Find which segment the current editor selection falls in.
 * Returns the sectionId or null if outside any segment.
 * Used to scope selection edits — Spec 08 §10.5.
 */
export function getSelectionSegmentId(editor: Editor): string | null {
  const { from, to } = editor.state.selection
  const doc = editor.state.doc

  let currentSectionId: string | null = null
  let selectedSectionId: string | null = null
  let foundStart = false
  let foundEnd = false

  doc.descendants((node, pos) => {
    if (foundStart && foundEnd) return false

    if (node.type.name === 'segmentHeading') {
      currentSectionId = (node.attrs['sectionId'] as string) ?? null
    }

    if (!foundStart && pos + node.nodeSize > from) {
      foundStart = true
      selectedSectionId = currentSectionId
    }

    if (!foundEnd && pos + node.nodeSize > to) {
      foundEnd = true
      // If end is in a different segment, selection spans segments
      if (currentSectionId !== selectedSectionId) {
        selectedSectionId = null
      }
    }

    return true
  })

  return selectedSectionId
}

/**
 * Check whether the editor selection spans multiple segment boundaries.
 * Used to reject cross-segment selection edits — Spec 08 §10.5.
 */
export function isSelectionCrossSegment(editor: Editor): boolean {
  const { from, to } = editor.state.selection
  if (from === to) return false

  const doc = editor.state.doc
  const segmentIds = new Set<string | null>()
  let currentSectionId: string | null = null

  doc.descendants((node, pos) => {
    if (node.type.name === 'segmentHeading') {
      currentSectionId = (node.attrs['sectionId'] as string) ?? null
    }

    const nodeStart = pos
    const nodeEnd = pos + node.nodeSize

    // Node overlaps with selection
    if (nodeEnd > from && nodeStart < to) {
      segmentIds.add(currentSectionId)
    }

    return true
  })

  // More than one unique segment in selection = cross-segment
  return segmentIds.size > 1
}

/**
 * Build a content snapshot map (sectionId → HTML) from segments array.
 * Used to initialize the previousSegments reference — Spec 08 §10.4.
 */
export function buildSegmentSnapshot(segments: DraftSegmentSummary[]): Map<string, string> {
  return new Map(segments.map((s) => [s.sectionId, s.content]))
}
